<template>
  <v-dialog :value="dialogProp.show" persistent max-width="900">
    <v-card class="elevation-12">
      <v-toolbar dense dark color="primary">
        <v-toolbar-title>
          {{ $t("Подача ценового предложения") }}
        </v-toolbar-title>
      </v-toolbar>
      <v-row v-if="!!generalError || isLoading" class="my-0 py-0 mx-1">
        <v-col v-if="isLoading" cols="12">
          <v-progress-linear :active="isLoading" color="blue" indeterminate />
        </v-col>
        <v-col v-if="!!generalError" class="mx-1">
          <general-error v-if="!!generalError" />
        </v-col>
      </v-row>
      <v-alert class="text-center" outlined :value="success" type="success">
        {{ $t("Заявка успешно внесена") }}
      </v-alert>
      <v-card-text class="pt-4">
        <ValidationObserver ref="order">
          <v-row class="pb-1 pt-0 my-0">
            <v-col class="py-2 my-0">
              <validatable-text-input
                  :value="order.premium"
                  :label="priceLabel"
                  :readonly="isLoading"
                  :prefix="null"
                  :rules="{
                    required: true,
                    max_value: MAX_FIX_PREMIUM_VALUE,
                    regex: /^(0|[1-9]\d*)$/
                  }"
                  :hint="premiumHint"
                  :tabindex="1"
                  data-type="float"
                  @change="changePremium" />
            </v-col>
            <v-col class="py-2 my-0">
              <validatable-text-input
                  v-model="order.partsCount"
                  :label="$t('Количество углеродных единиц')"
                  :readonly="isLoading"
                  :rules="{
                    required: true,
                    regex: /^[0-9]{1,11}?$/,
                    min_value: 1,
                    max_value: dialogProp.params.maxPartsCount
                  }"
                  :tabindex="1"
                  persistent-hint
                  :hint="partsCountHint"
                  data-type="float" />
            </v-col>
          </v-row>
          <v-row class="pb-1 pt-0 my-0">
            <v-col cols="12" md="12" class="pl-2">
              <validatable-checkbox
                  v-model="isClient"
                  :label="$t('Клиентская')"
                  @change="clearClientInfo" />
            </v-col>
          </v-row>
          <v-row class="pb-1 pt-0 my-0">
            <v-col cols="12" md="6" class="pl-2">
              <validatable-dynamic-text-input
                  v-if="isClient"
                  v-model="order.clientOrganizationInn"
                  v-test="{ input: 'clientOrganizationInn' }"
                  :readonly="!isClient"
                  rules="required"
                  dense
                  :label="$t('ИНН организации Клиента')" />
            </v-col>
            <v-col cols="12" md="6" class="pl-2">
              <validatable-dynamic-text-input
                  v-if="isClient"
                  v-model="order.clientOrganizationFullName"
                  v-test="{ input: 'clientOrganizationFullName' }"
                  rules="required"
                  dense
                  :readonly="!isClient"
                  :label="$t('Полное наименование организации Клиента')" />
            </v-col>
            <v-col v-if="isClient">
              <text-view
                  cols="2"
                  :display="' '"
                  :label="$t('Доверенность Клиента')" />
              <v-col
                  v-if="file.fileId"
                  cols="2"
                  class="ma-0 pa-0"
                  align-self="start">
                <text-view v-if="file.fileId" :display="file.fileName" />
                <btn-icon-tip
                    :disabled="isLoading"
                    icon="delete"
                    :tip="$t('Удалить документ')"
                    @click="deleteFile(file.fileId)" />
              </v-col>
              <v-col v-if="!file.fileId" cols="1" class="ma-0 pa-0">
                <typed-file-upload
                    :disabled="isLoading"
                    accept=".doc,.docx,.xls,.xlsx,.pdf"
                    :document-type="documentType"
                    :document-group="documentGroup"
                    :upload-method="uploadSingleBusinessFile" />
              </v-col>
            </v-col>
          </v-row>
          <v-alert
              class="text-center"
              outlined
              :value="!!errorMessage"
              type="error">
            {{ errorMessage }}
          </v-alert>

          <v-row>
            <v-col class="text-left py-0">
              <validatable-checkbox
                  v-model="approve"
                  :readonly="isLoading"
                  rules="required"
                  :label="conditionLabel" />
            </v-col>
          </v-row>
        </ValidationObserver>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn id="cancelConfirmBtn" color="primary" @click="cancel">
          {{ $t("Закрыть") }}
        </v-btn>
        <v-btn
            id="continueOperationBtn"
            color="primary"
            :disabled="!approve || isLoading || !!errorMessage"
            @click.stop="continueOperation">
          {{ $t("Подать") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import GeneralError from '@/components/reused/common/GeneralError.vue'
  import ValidatableTextInput from '@/components/reused/ui/ValidatableTextInput.vue'
  import ValidatableDynamicTextInput from '@/components/reused/ui/ValidatableDynamicTextInput.vue'
  import ValidatableCheckbox from '@/components/reused/ui/ValidatableCheckbox.vue'
  import TypedFileUpload from '@/components/reused/ui/TypedFileUpload.vue'
  import TextView from '@/components/reused/ui/TextView.vue'
  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import {
    MAX_FIX_PREMIUM_VALUE,
    MIN_FIX_PREMIUM_VALUE,
    MAX_DIFF_PREMIUM_VALUE,
    MIN_DIFF_PREMIUM_VALUE,
    DOCUMENT_TYPES,
    BUSINESS_DOCUMENT_GROUPS
  } from '@/constants/business'
  import { mapActions, mapState } from 'vuex'
  import {
    CURRENT_ORGANIZATION,
    CURRENT_USER,
    LOT_ORDERS,
    BUSINESS_FILES
  } from '@/constants/store.js'

  export default {
    components: {
      GeneralError,
      ValidatableTextInput,
      ValidatableDynamicTextInput,
      ValidatableCheckbox,
      TypedFileUpload,
      TextView,
      BtnIconTip
    },
    props: {
      dialogProp: {
        type: Object,
        default: () => {
          return {
            params: {
              maxPartsCount: null,
              onePartVolume: null,
              unit: null,
              priceStart: null,
              sessionId: null,
              sessionType: null,
              lotId: null,
              lotType: null
            },
            callback: null,
            show: false
          }
        }
      }
    },
    data () {
      return {
        MAX_FIX_PREMIUM_VALUE,
        MIN_FIX_PREMIUM_VALUE,
        MAX_DIFF_PREMIUM_VALUE,
        MIN_DIFF_PREMIUM_VALUE,
        isLoading: false,
        success: false,
        approve: false,
        documentType: DOCUMENT_TYPES.OFFER_CLIENT_PROXY,
        documentGroup: BUSINESS_DOCUMENT_GROUPS.OFFER_CLIENT_PROXY,
        order: {
          fileId: null,
          partsCount: null,
          premium: null,
          clientOrganizationInn: null,
          clientOrganizationFullName: null
        },
        isClient: false
      }
    },
    computed: {
      ...mapState(CURRENT_USER.NAMESPACE, {
        currentUser: state => state || {}
      }),
      ...mapState(CURRENT_ORGANIZATION.NAMESPACE, {
        organization: state =>
          state[CURRENT_ORGANIZATION.STATE.ORGANIZATION] || {}
      }),
      ...mapState(BUSINESS_FILES.NAMESPACE, {
        file: state => state[BUSINESS_FILES.STATE.CURRENT_FILE] || {}
      }),
      headers () {
        return [
          {
            text: this.$t('Наименование позиции'),
            align: 'start',
            sortable: false,
            value: 'positionDescription'
          },
          {
            text: this.$t('Цена'),
            align: 'start',
            sortable: false,
            value: 'premium'
          }
        ]
      },
      maxVolume () {
        return (
          this.dialogProp.params.maxPartsCount *
          this.dialogProp.params.onePartVolume
        )
      },
      partsCountHint () {
        return this.$t(
          'Максимальное количество единиц в одни руки {0} ({1} {2})',
          [
            this.dialogProp.params.maxPartsCount,
            this.maxVolume,
            this.$t(this.dialogProp.params.unit)
          ]
        )
      },
      errorMessage () {
        if (
          !!this.order.partsCount &&
          this.dialogProp.params.maxPartsCount < this.order.partsCount
        ) {
          const overPartsCount =
            this.order.partsCount - this.dialogProp.params.maxPartsCount
          const overVolume =
            this.order.partsCount * this.dialogProp.params.onePartVolume -
            this.maxVolume
          return this.$t(
            'Превышен объем отпуска продукции на {0} {2} (количество углеродных единиц превышено на {1})',
            [overVolume, overPartsCount, this.dialogProp.params.unit]
          )
        }
        return null
      },
      priceLabel () {
        return this.$t('Цена 1 (одной) углеродной единицы (с учетом НДС)')
      },
      premiumHint () {
        return this.$t('Стартовое значение цены: {0}', [
          this.dialogProp.params.priceStart
        ])
      },
      conditionLabel () {
        if (this.isClient) {
          return this.$t(
            'Признаем, что поданное Уполномоченным представителем {0}, ОГРН: {1} {2} ценовое предложение является предложением Клиента участника: {3}, ИНН {4}, содержащим всю необходимую информацию для заключения Договора. Настоящее ценовое предложение является уведомлением о безусловном согласии на заключение Договора на условиях, содержащихся в настоящем ценовом предложении, в случае его акцепта Организатором.',
            [
              this.organization.organizationShortName,
              this.organization.ogrn,
              this.currentUser.contactPerson,
              this.order.clientOrganizationFullName,
              this.order.clientOrganizationInn
            ]
          )
        } else {
          return this.$t(
            'Признаем, что поданное {0} ценовое предложение в электронной форме является предложением {1}, ОГРН: {2}, содержащим всю необходимую информацию для заключения Договора. Настоящее ценовое предложение является уведомлением о безусловном согласии на заключение Договора на условиях, содержащихся в настоящем ценовом предложении, в случае ее акцепта Организатором.',
            [
              this.currentUser.contactPerson,
              this.organization.organizationShortName,
              this.organization.ogrn
            ]
          )
        }
      }
    },
    created () {
      if (!this.organization.organizationId) {
        this.getCurrentOrganization()
      }
      this.order.premium = this.dialogProp.params.nextPrice
    },
    beforeDestroy () {
      this.clearSingleBusinessFileAction()
      this.clearCurrentOrganizationAction()
    },
    methods: {
      ...mapActions(CURRENT_ORGANIZATION.NAMESPACE, {
        getCurrentOrganizationAction:
          CURRENT_ORGANIZATION.ACTIONS.GET_CURRENT_ORGANIZATION,
        clearCurrentOrganizationAction:
          CURRENT_ORGANIZATION.ACTIONS.CLEAR_CURRENT_ORGANIZATION
      }),
      ...mapActions(LOT_ORDERS.NAMESPACE, {
        addOrderDivisibleVolumeAction:
          LOT_ORDERS.ACTIONS.ADD_ORDER_DIVISIBLE_VOLUME
      }),
      ...mapActions(BUSINESS_FILES.NAMESPACE, {
        uploadSingleBusinessFileAction:
          BUSINESS_FILES.ACTIONS.UPLOAD_SINGLE_BUSINESS_FILE,
        deleteBusinessFileAction: BUSINESS_FILES.ACTIONS.DELETE_BUSINESS_FILE,
        clearSingleBusinessFileAction:
          BUSINESS_FILES.ACTIONS.CLEAR_SINGLE_BUSINESS_FILE
      }),
      uploadSingleBusinessFile: async function (
        form,
        documentType,
        documentGroup
      ) {
        // try catch не нужен (если ошибка, обработаем в месте вызова метода)

        if (this.isAdmin) {
          form.append('creatorId', this.dialogProp.lot.creatorId)
        }
        try {
          this.isLoading = true
          await this.uploadSingleBusinessFileAction({
            form,
            documentType,
            documentGroup
          })
          this.order.fileId = this.file.fileId
        } finally {
          this.isLoading = false
        }
      },
      deleteFile: async function (fileId) {
        try {
          this.isLoading = true
          await this.deleteBusinessFileAction({ fileId })
          await this.clearSingleBusinessFileAction()
          this.order.fileId = null
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfUnknownError(error)
        }
      },
      getCurrentOrganization: async function () {
        const vm = this
        vm.isLoading = true
        try {
          await this.getCurrentOrganizationAction()
        } catch (error) {
          vm.throwIfNotServerError(error)
        } finally {
          vm.isLoading = false
        }
      },
      continueOperation: async function () {
        await this.hideErrorAction()
        try {
          const result = await this.$refs.order.validate()
          if (!result) {
            return
          }
          this.isLoading = true
          this.success = false
          await this.addOrderDivisibleVolumeAction({
            lotId: this.dialogProp.params.lotId,
            lotType: this.dialogProp.params.lotType,
            sessionType: this.dialogProp.params.sessionType,
            sessionId: this.dialogProp.params.sessionId,
            order: this.order
          })
          this.success = true
          this.isLoading = false
          if (this.dialogProp.callback) {
            await this.dialogProp.callback()
            this.cancel()
          }
        } catch (error) {
          this.isLoading = false
          this.throwIfUnknownError(error)
        }
      },
      cancel: function () {
        this.$emit('close')
      },
      changePremium: function (premium) {
        if (typeof premium === 'number') {
          this.order.premium = premium
        }
      },
      clearClientInfo: function (value) {
        if (value === false) {
          this.order.clientOrganizationFullName = null
          this.order.clientOrganizationInn = null
          if (this.file.fileId) {
            this.deleteFile(this.file.fileId)
          }
        }
      }
    }
  }
</script>
